import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  AtribuirPlanoMasterDTO,
  ConcederUsuariosMasterDTO,
  MasterCicloHistoricoDTO,
  MasterEmpresaResumoDTO,
  MasterUsuariosGlobaisResponseDTO,
  MasterUsuariosResponseDTO,
  FormaCobrancaPlanoEspecial,
  PlanoEspecialMasterDTO,
} from '../../models/master/master-empresa.model';
import { MasterSessaoDTO } from '../../models/master/master-sessao.model';
import {
  FiltroPlanosMaster,
  MasterPlanosResponseDTO,
  OrdemPlanosMaster,
  TipoPlanoMaster,
  CategoriaPlanoMaster,
} from '../../models/master/master-plano.model';
import {
  ConsultaDashboardMasterParams,
  ConsultaCobrancasMasterParams,
  ConsultaRiscoMasterParams,
  MasterCobrancaDTO,
  MasterDashboardAssinaturasDTO,
  MasterRiscoResponseDTO,
} from '../../models/master/master-dashboard.model';

/**
 * Area Master: consulta e operacao sobre a empresa de um cliente.
 *
 * A empresa alvo vai SEMPRE no path — o master continua sendo ele mesmo, na propria empresa,
 * enquanto opera. Todos os endpoints exigem `categoriaAcessoMaster` e respondem 403 sem ele.
 */
@Injectable({
  providedIn: 'root',
})
export class MasterEmpresaService implements ApiInterface {

  constructor(private apiService: ApiService) {}

  // --- Metodos padrao ApiInterface ---

  get<T>(url: string, data?: any): Observable<any> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<any>(path);
  }

  post<T>(url: string, data: any): Observable<any> {
    return this.apiService.create<any>(url, data);
  }

  put<T>(url: string, data: any): Observable<any> {
    return this.apiService.update<any>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  // --- Metodos especificos ---

  /**
   * GET /master/empresas/{empresaId}/resumo — ficha da empresa: configuracao, assinatura
   * vigente e vagas de usuario.
   */
  consultarResumo(empresaId: number): Observable<MasterEmpresaResumoDTO> {
    return this.apiService.read<MasterEmpresaResumoDTO>(
      `/master/empresas/${empresaId}/resumo`
    );
  }

  /**
   * GET /master/empresas/{empresaId}/ciclos — histórico de ciclos de 30 dias da empresa
   * (plano, valores, teto de vendas e acessos de cada janela), do mais recente para o mais
   * antigo. Lista vazia quando a empresa nunca contratou.
   */
  listarHistoricoDeCiclos(
    empresaId: number,
    limite?: number,
  ): Observable<MasterCicloHistoricoDTO[]> {
    const queryParams = this.apiService.buildQueryParams({ limite } as any);
    const path = queryParams
      ? `/master/empresas/${empresaId}/ciclos?${queryParams}`
      : `/master/empresas/${empresaId}/ciclos`;
    return this.apiService.read<MasterCicloHistoricoDTO[]>(path);
  }

  /**
   * GET /master/empresas/{empresaId}/usuarios — usuarios de acesso da empresa (Spring Page).
   *
   * Pagina vazia e resposta valida: empresa sem usuario nenhum nao e erro.
   */
  listarUsuarios(
    empresaId: number,
    page: number,
    size: number,
    sort?: string,
  ): Observable<MasterUsuariosResponseDTO> {
    const params: Record<string, unknown> = { page, size };
    if (sort) {
      params['sort'] = sort;
    }
    const queryParams = this.apiService.buildQueryParams(params);
    return this.apiService.read<MasterUsuariosResponseDTO>(
      `/master/empresas/${empresaId}/usuarios?${queryParams}`
    );
  }

  /**
   * GET /master/usuarios?busca= — busca usuarios em TODAS as empresas.
   *
   * Caminho de quem so tem o e-mail (ou o nome) de quem ligou: acha a pessoa e, com ela, a
   * empresa a abrir. Sem `busca`, lista todos paginado.
   */
  buscarUsuarios(
    busca: string,
    page: number,
    size: number,
    sort?: string,
  ): Observable<MasterUsuariosGlobaisResponseDTO> {
    const params: Record<string, unknown> = { page, size };
    if (busca && busca.trim().length > 0) {
      params['busca'] = busca.trim();
    }
    if (sort) {
      params['sort'] = sort;
    }
    const queryParams = this.apiService.buildQueryParams(params);
    return this.apiService.read<MasterUsuariosGlobaisResponseDTO>(
      `/master/usuarios?${queryParams}`
    );
  }

  /**
   * GET /master/planos — carteira de assinaturas ativas, uma linha por empresa.
   *
   * TRES EIXOS de recorte, independentes e combinaveis (intersecao):
   *  - `filtro`    — situacao da assinatura (TODOS, EM_DIA, VENCENDO, NO_LIMITE, EM_CARENCIA,
   *                  VENCIDOS, RENOVACAO);
   *  - `tipoPlano` — periodicidade contratada. AUSENTE = todos os tipos; NAO existe valor TODOS.
   *  - `categoria` — faixa do plano (BRONZE, PRATA, OURO) ou SEM_INICIAL. Mesmas regras de `tipoPlano`.
   *
   * A ordenacao vai por parametro proprio (e nao no formato `sort` do Spring) porque as colunas
   * que interessam — vencimento e uso do limite — sao calculadas na consulta, e nao campos de
   * entidade.
   */
  listarPlanosAtivos(
    busca: string,
    filtro: FiltroPlanosMaster,
    ordenarPor: OrdemPlanosMaster,
    ascendente: boolean,
    page: number,
    size: number,
    percentualAlerta?: number,
    diasParaVencer?: number,
    tipoPlano?: TipoPlanoMaster | null,
    categoria?: CategoriaPlanoMaster | null,
  ): Observable<MasterPlanosResponseDTO> {
    const params: Record<string, unknown> = { page, size, filtro, ordenarPor, ascendente };
    if (busca && busca.trim().length > 0) {
      params['busca'] = busca.trim();
    }
    if (percentualAlerta != null) {
      params['percentualAlerta'] = percentualAlerta;
    }
    if (diasParaVencer != null) {
      params['diasParaVencer'] = diasParaVencer;
    }
    // GUARD TRUTHY — NAO copiar o `!= null` das duas linhas acima (DIV-01).
    // `ApiService.buildQueryParams` descarta APENAS `undefined`:
    //   null -> "tipoPlano=null" (a string "null", que nao esta no enum e quebra o binding)
    //   ''   -> "tipoPlano="
    // As duas linhas acima usam `!= null` porque sao `number` e precisam deixar o ZERO passar;
    // aqui e string de lista fechada, e vazio nao e valor: e ausencia.
    if (tipoPlano) {
      params['tipoPlano'] = tipoPlano;
    }
    // Mesmo guard truthy de `tipoPlano`, pelo mesmo motivo.
    if (categoria) {
      params['categoria'] = categoria;
    }
    const queryParams = this.apiService.buildQueryParams(params);
    return this.apiService.read<MasterPlanosResponseDTO>(`/master/planos?${queryParams}`);
  }

  /**
   * GET /master/planos/dashboard — faixa de indicadores da Area Master.
   *
   * `de`/`ate` entram em ISO `yyyy-MM-dd` e sao enviados JUNTOS ou NENHUM (DF-06): mandar so
   * `ate` faz o backend calcular `de = ate - 30` em silencio (contrato §2.1), e a tela passaria
   * a exibir uma janela que nao pediu.
   *
   * `diasSemPagar`, `diasSemMovimentar` e `diasCobrancaPendente` normalmente NAO sao enviados
   * (DF-04): a tela nao os controla e le os ECOS da resposta.
   *
   * Parametros em OBJETO, e nao posicionais: sao 7 opcionais, 5 deles `number` — trocar dois
   * compilaria, responderia 200 e produziria o card errado.
   */
  consultarDashboard(
    params: ConsultaDashboardMasterParams,
  ): Observable<MasterDashboardAssinaturasDTO> {
    const query: Record<string, unknown> = {};

    const de = params.de?.trim();
    const ate = params.ate?.trim();
    if (de && ate) {
      query['de'] = de;
      query['ate'] = ate;
    }

    if (params.diasParaVencer != null) {
      query['diasParaVencer'] = params.diasParaVencer;
    }
    if (params.percentualAlerta != null) {
      query['percentualAlerta'] = params.percentualAlerta;
    }
    if (params.diasSemPagar != null) {
      query['diasSemPagar'] = params.diasSemPagar;
    }
    if (params.diasSemMovimentar != null) {
      query['diasSemMovimentar'] = params.diasSemMovimentar;
    }
    if (params.diasCobrancaPendente != null) {
      query['diasCobrancaPendente'] = params.diasCobrancaPendente;
    }

    const queryParams = this.apiService.buildQueryParams(query);
    const path = queryParams
      ? `/master/planos/dashboard?${queryParams}`
      : '/master/planos/dashboard';
    return this.apiService.read<MasterDashboardAssinaturasDTO>(path);
  }

  /**
   * GET /master/planos/risco — drill-down paginado das empresas em risco.
   *
   * `diasSemPagar` / `diasSemMovimentar` devem vir dos ECOS do dashboard, senao `totalElements`
   * deixa de bater com o card. Cada um so viaja no tipo de risco que o consome.
   *
   * `busca` so entra com conteudo — e, quando entra, QUEBRA a igualdade com o card de proposito
   * (a tela avisa o operador).
   */
  listarEmpresasEmRisco(
    params: ConsultaRiscoMasterParams,
  ): Observable<MasterRiscoResponseDTO> {
    const query: Record<string, unknown> = {
      tipoRisco: params.tipoRisco,
      page: params.page,
      size: params.size,
    };

    if (params.tipoRisco === 'INADIMPLENCIA' && params.diasSemPagar != null) {
      query['diasSemPagar'] = params.diasSemPagar;
    }
    if (params.tipoRisco === 'ABANDONO' && params.diasSemMovimentar != null) {
      query['diasSemMovimentar'] = params.diasSemMovimentar;
    }

    const busca = params.busca?.trim();
    if (busca) {
      query['busca'] = busca;
    }

    const queryParams = this.apiService.buildQueryParams(query);
    return this.apiService.read<MasterRiscoResponseDTO>(`/master/planos/risco?${queryParams}`);
  }
  /**
   * GET /master/planos/cobrancas — drill-down dos cards "Total recebido" e "Cobranca emitida e
   * nao paga". Sem paginacao: a lista e do tamanho do card.
   *
   * `de`/`ate` so viajam em RECEBIDAS e devem ser os ECOS do dashboard, senao a soma da lista
   * deixa de bater com o card.
   */
  listarCobrancas(params: ConsultaCobrancasMasterParams): Observable<MasterCobrancaDTO[]> {
    const query: Record<string, unknown> = { tipo: params.tipo };
    if (params.tipo === 'RECEBIDAS' && params.de && params.ate) {
      query['de'] = params.de;
      query['ate'] = params.ate;
    }
    const queryParams = this.apiService.buildQueryParams(query);
    return this.apiService.read<MasterCobrancaDTO[]>(`/master/planos/cobrancas?${queryParams}`);
  }


  /**
   * GET /master/empresas/{empresaId}/sessoes — sessoes de acesso dos usuarios da empresa.
   *
   * Lista simples, sem paginacao: sao no maximo duas por usuario (uma web e uma mobile).
   */
  listarSessoes(empresaId: number): Observable<MasterSessaoDTO[]> {
    return this.apiService.read<MasterSessaoDTO[]>(
      `/master/empresas/${empresaId}/sessoes`
    );
  }

  /**
   * DELETE /master/empresas/{empresaId}/sessoes/{sessaoId} — encerra a sessao e libera a vaga
   * na hora, sem esperar a inatividade. Responde 204.
   */
  encerrarSessao(empresaId: number, sessaoId: number): Observable<void> {
    return this.apiService.delete(
      `/master/empresas/${empresaId}/sessoes/${sessaoId}`
    );
  }

  /**
   * PATCH /master/empresas/{empresaId}/plano — concede um plano SEM cobranca.
   *
   * Devolve a ficha recalculada: a tela mostra o que ficou gravado, nao o que pediu.
   */
  atribuirPlano(empresaId: number, dto: AtribuirPlanoMasterDTO): Observable<MasterEmpresaResumoDTO> {
    return this.apiService.patch<MasterEmpresaResumoDTO>(
      `/master/empresas/${empresaId}/plano`,
      dto
    );
  }

  /**
   * POST /master/empresas/{empresaId}/plano-especial — concede um plano especial negociado
   * (faixa, valor, prazo em dias e limite de vendas proprios), SEM cobranca.
   *
   * Vale tambem para empresa que nao tem plano nenhum: a inscricao nasce nesta chamada.
   * Devolve a ficha recalculada, como as demais concessoes.
   */
  concederPlanoEspecial(
    empresaId: number,
    dto: PlanoEspecialMasterDTO,
  ): Observable<MasterEmpresaResumoDTO> {
    return this.apiService.create<MasterEmpresaResumoDTO>(
      `/master/empresas/${empresaId}/plano-especial`,
      dto
    );
  }

  /**
   * POST /master/empresas/{empresaId}/plano-especial/cobranca?forma= — gera a cobranca do mesmo
   * pacote negociado.
   *
   * O plano so passa a valer quando a Bancard confirmar: a intencao volta com `urlQr`/`qrData`
   * (QR) ou `linkUrl`/`linkAlias` (cartao), e a tela acompanha pelo polling de sempre.
   */
  cobrarPlanoEspecial(
    empresaId: number,
    dto: PlanoEspecialMasterDTO,
    forma: FormaCobrancaPlanoEspecial,
  ): Observable<any> {
    return this.apiService.create<any>(
      `/master/empresas/${empresaId}/plano-especial/cobranca?forma=${forma}`,
      dto
    );
  }

  /**
   * PATCH /master/empresas/{empresaId}/usuarios-concedidos — define o TOTAL de acessos, sem
   * cobranca e sem trocar o plano.
   */
  concederUsuarios(
    empresaId: number,
    dto: ConcederUsuariosMasterDTO,
  ): Observable<MasterEmpresaResumoDTO> {
    return this.apiService.patch<MasterEmpresaResumoDTO>(
      `/master/empresas/${empresaId}/usuarios-concedidos`,
      dto
    );
  }
}
